/**
 * Accounting Selectors - Pure functions for derived data
 *
 * Centralized here so they are:
 * - Reusable from both the (deprecated) Zustand store and the React Query hooks
 * - Independently unit-testable (no React, no store, no network)
 * - Free of barrel-export conflicts: import directly from this module.
 *
 * Convention: each function takes the data it needs as arguments; it does not
 * read from any store or hook. This keeps the functions pure and easy to test.
 */

import type { CentsAmount, InvoiceItem, Movement, Invoice } from '../domain/models';

// =============================================================================
// FORMATTING
// =============================================================================

/**
 * Format a CentsAmount as a localized EUR string.
 * Example: 12345 -> "€ 123,45"
 */
export function formatEUR(cents: CentsAmount): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

// =============================================================================
// INVOICE TOTALS
// =============================================================================

/**
 * Compute the grand total and VAT total of an invoice from its line items.
 * Pure: each item is quantity * unitPrice (cents), VAT = round(lineTotal * vatRate).
 */
export function calculateInvoiceTotal(items: InvoiceItem[]): { total: CentsAmount; vatTotal: CentsAmount } {
  let subtotal = 0;
  let vatTotal = 0;

  for (const item of items) {
    const lineTotal = item.quantity * item.unitPrice;
    subtotal += lineTotal;
    vatTotal += Math.round(lineTotal * item.vatRate);
  }

  return {
    total: subtotal + vatTotal,
    vatTotal,
  };
}

// =============================================================================
// MOVEMENT FILTERS
// =============================================================================

/**
 * Filter movements according to the active sidebar key.
 * Pure: identical signature to the original store selector, but stateless.
 */
export function getFilteredMovements(movements: Movement[], filterKey: string): Movement[] {
  switch (filterKey) {
    case 'in':
      return movements.filter((m) => m.direction === 'in');
    case 'out':
      return movements.filter((m) => m.direction === 'out');
    case 'unreconciled':
      return movements.filter((m) => !m.reconciled);
    default:
      return movements;
  }
}

// =============================================================================
// INVOICE FILTERS
// =============================================================================

/**
 * Filter invoices according to the active sidebar key.
 */
export function getFilteredInvoices(invoices: Invoice[], filterKey: string): Invoice[] {
  switch (filterKey) {
    case 'invoices-issued':
      return invoices.filter((i) => i.direction === 'issued');
    case 'invoices-received':
      return invoices.filter((i) => i.direction === 'received');
    case 'invoices-overdue':
      return invoices.filter((i) => i.status === 'overdue');
    case 'invoices-paid':
      return invoices.filter((i) => i.status === 'paid');
    default:
      return invoices;
  }
}

// =============================================================================
// BALANCES
// =============================================================================

/**
 * Compute the running balance for a specific account from its movements.
 * `in` adds, `out` subtracts.
 */
export function getAccountBalance(movements: Movement[], accountId: string): CentsAmount {
  return movements
    .filter((m) => m.accountId === accountId)
    .reduce((sum, m) => {
      return m.direction === 'in' ? sum + m.amount : sum - m.amount;
    }, 0);
}

// =============================================================================
// INVOICE DERIVATIONS
// =============================================================================

/**
 * Return invoices past their due date and not yet paid or cancelled.
 * Uses the current clock — not pure, but deterministic given a `now` reference.
 */
export function getOverdueInvoices(invoices: Invoice[], now: Date = new Date()): Invoice[] {
  return invoices.filter((i) => {
    if (i.status === 'paid' || i.status === 'cancelled') return false;
    if (!i.dueAt) return false;
    return new Date(i.dueAt) < now;
  });
}

/**
 * Sum totals of issued invoices still pending (sent or overdue).
 */
export function getPendingInvoicesTotal(invoices: Invoice[]): CentsAmount {
  return invoices
    .filter((i) => i.direction === 'issued' && (i.status === 'sent' || i.status === 'overdue'))
    .reduce((sum, i) => sum + i.total, 0);
}
